import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { PALETTES } from './palettes.js';
import { APPEARANCES, APPEARANCE_LABELS, DARK_PALETTE_KEY, isDarkPalette } from './mode.js';
import { TEXT_SCALES, TRISTATES, TRISTATE_LABELS } from './a11y.js';
import { useTheme } from './react.js';

/**
 * Menu « Accessibilité » — le même dans SEmplyPrevi et SEmplyCompta.
 *
 * Un bouton de barre qui ouvre un panneau regroupant tout ce qui règle le
 * confort de lecture : l'apparence (clair / sombre / navigateur), la palette
 * — dont « Daltonien », que SEmplyApp proposait déjà dans ses paramètres —,
 * la taille du texte, le contraste renforcé et la réduction des animations.
 *
 * Les commandes sont des `<input>` natifs (radios, cases à cocher) dans des
 * `<fieldset>` : c'est ce que les lecteurs d'écran restituent le mieux — le
 * groupe est nommé par sa `<legend>`, l'état est porté par l'élément lui-même
 * et non par une classe. Le panneau est un `role="dialog"` non modal : Échap
 * et le clic dehors le ferment, le focus revient au bouton.
 *
 * Deux contrôleurs et non un : le thème (createThemeController) et les
 * réglages d'accessibilité (createAccessibilityController) sont indépendants,
 * chacun avec son stockage. Le menu ne fait que les présenter côte à côte.
 */
// Contrôleur inerte, pour un produit qui ne confie pas son thème au menu
// (SEmplyApp, dont le thème vit dans un store Zustand) : les hooks doivent
// être appelés inconditionnellement, on leur donne quelque chose à lire.
const SNAPSHOT_VIDE = Object.freeze({ mode: 'auto', paletteKey: 'default', lightPaletteKey: 'default', appearance: 'system' });
const SANS_THEME = { subscribe: () => () => {}, getSnapshot: () => SNAPSHOT_VIDE };

export function AccessibilityMenu({
  /** Contrôleur de thème. Absent : les sections Apparence et Palette sont masquées. */
  theme = null,
  a11y,
  label = 'Accessibilité',
  className = '',
  /** Masque le choix de palette (produit à palette verrouillée). */
  hidePalettes = false,
}) {
  const [ouvert, setOuvert] = useState(false);
  const [alignement, setAlignement] = useState('end');
  const boite = useRef(null);
  const panneau = useRef(null);
  const bouton = useRef(null);
  const id = useId();

  const t = useTheme(theme ?? SANS_THEME);
  const a = useTheme(a11y);

  // Fermeture au clic dehors et à Échap, écouteurs posés seulement à l'ouverture.
  useEffect(() => {
    if (!ouvert) return undefined;
    const auClicDehors = (e) => {
      if (boite.current && !boite.current.contains(e.target)) setOuvert(false);
    };
    const auClavier = (e) => {
      if (e.key === 'Escape') {
        setOuvert(false);
        bouton.current?.focus();
      }
    };
    document.addEventListener('mousedown', auClicDehors);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClicDehors);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  // Alignement mesuré (cf. SuiteSwitcher) et focus posé dans le panneau :
  // sans ce déplacement, un utilisateur au clavier ouvrirait un panneau qu'il
  // ne peut atteindre qu'en tabulant à l'aveugle.
  useLayoutEffect(() => {
    if (!ouvert) {
      setAlignement('end');
      return;
    }
    const rect = panneau.current?.getBoundingClientRect();
    if (rect && rect.left < 8) setAlignement('start');
    panneau.current?.querySelector('input:checked, input, button')?.focus();
  }, [ouvert]);

  const palettes = Object.entries(PALETTES).filter(([key, p]) => {
    if (p.internal) return false;
    // En automatique, la sombre est servie par le système, pas choisie.
    if (t.mode === 'auto' && isDarkPalette(key)) return false;
    return true;
  });
  const paletteCochee = t.mode === 'auto' ? t.lightPaletteKey : t.paletteKey;

  return (
    <div className={`a11y-menu ${className}`.trim()} ref={boite}>
      <button
        ref={bouton}
        type="button"
        className="a11y-menu-btn"
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        aria-controls={`${id}-panel`}
        onClick={() => setOuvert((v) => !v)}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" className="a11y-menu-icon">
          <circle cx="12" cy="4.5" r="2" fill="currentColor" />
          <path
            d="M4.5 8.5h15a1 1 0 0 1 0 2l-5 .8v3.2l2.6 6.3a1 1 0 0 1-1.85.77L12 15.9l-3.25 5.67a1 1 0 0 1-1.85-.77l2.6-6.3v-3.2l-5-.8a1 1 0 0 1 0-2z"
            fill="currentColor"
          />
        </svg>
        <span className="a11y-menu-btn-label">{label}</span>
      </button>

      {ouvert && (
        <div
          ref={panneau}
          id={`${id}-panel`}
          className={`a11y-menu-panel${alignement === 'start' ? ' a11y-menu-panel-start' : ''}`}
          role="dialog"
          aria-label={label}
        >
          {/* ── Apparence ── */}
          {theme && (
          <fieldset className="a11y-group">
            <legend>Apparence</legend>
            <div className="a11y-choices">
              {APPEARANCES.map((choice) => (
                <label key={choice} className="a11y-choice">
                  <input
                    type="radio"
                    name={`${id}-appearance`}
                    value={choice}
                    checked={t.appearance === choice}
                    onChange={() => theme.setAppearance(choice)}
                  />
                  <span>{APPEARANCE_LABELS[choice]}</span>
                </label>
              ))}
            </div>
          </fieldset>
          )}

          {/* ── Palette ── */}
          {theme && !hidePalettes && (
            <fieldset className="a11y-group">
              <legend>{t.mode === 'auto' ? 'Palette claire' : 'Palette'}</legend>
              {t.mode === 'auto' && (
                <p className="a11y-hint">
                  Utilisée quand le système est en thème clair ; en sombre, la palette
                  «&nbsp;{PALETTES[DARK_PALETTE_KEY]?.label}&nbsp;» est servie.
                </p>
              )}
              <div className="a11y-palettes">
                {palettes.map(([key, p]) => (
                  // `data-libelle` : bulle du libellé complet au survol, au
                  // focus et à l'appui (12/10/2026) — dans un panneau étroit,
                  // « Par défaut » ou « Daltonien » sont coupés.
                  <label key={key} className="a11y-palette" data-libelle={p.label}>
                    <input
                      type="radio"
                      name={`${id}-palette`}
                      value={key}
                      checked={paletteCochee === key}
                      onChange={() => theme.setPalette(key)}
                    />
                    <span className="a11y-swatches" aria-hidden="true">
                      {p.swatch.map((c, i) => (
                        <span key={i} className="a11y-swatch" style={{ backgroundColor: c }} />
                      ))}
                    </span>
                    <span className="a11y-palette-label">{p.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {/* ── Taille du texte ── */}
          <fieldset className="a11y-group">
            <legend>Taille du texte</legend>
            <div className="a11y-choices">
              {TEXT_SCALES.map((scale) => (
                <label key={scale} className="a11y-choice">
                  <input
                    type="radio"
                    name={`${id}-scale`}
                    value={scale}
                    checked={a.textScale === scale}
                    onChange={() => a11y.setTextScale(scale)}
                  />
                  <span>{Math.round(scale * 100)}&nbsp;%</span>
                </label>
              ))}
            </div>
            <p className="a11y-hint">S’ajoute au zoom du navigateur.</p>
          </fieldset>

          {/* ── Contraste ── */}
          <Tristate
            id={`${id}-contrast`}
            legend="Contraste renforcé"
            value={a.contrast}
            systemOn={a.systemContrast}
            onChange={(v) => a11y.setContrast(v)}
          />

          {/* ── Animations ── */}
          <Tristate
            id={`${id}-motion`}
            legend="Réduire les animations"
            value={a.motion}
            systemOn={a.systemMotion}
            onChange={(v) => a11y.setMotion(v)}
          />

          <div className="a11y-footer">
            <button type="button" className="a11y-reset" onClick={() => a11y.reset()}>
              Réinitialiser
            </button>
            <button
              type="button"
              className="a11y-close"
              onClick={() => {
                setOuvert(false);
                bouton.current?.focus();
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Réglage à trois positions : suivre le système · activé · désactivé. */
function Tristate({ id, legend, value, systemOn, onChange }) {
  return (
    <fieldset className="a11y-group">
      <legend>{legend}</legend>
      <div className="a11y-choices">
        {TRISTATES.map((v) => (
          <label key={v} className="a11y-choice">
            <input
              type="radio"
              name={id}
              value={v}
              checked={value === v}
              onChange={() => onChange(v)}
            />
            <span>
              {TRISTATE_LABELS[v]}
              {v === 'auto' && (
                <span className="a11y-hint-inline"> ({systemOn ? 'activé' : 'désactivé'})</span>
              )}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
